/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Apr 05 2024
 **/

import { WheelEvent, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { TransformComponent, TransformWrapper, ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import { debounce } from "lodash-es";

import { EventHandler } from "@rfice/eventhandler";
import { _T } from "@rfice/language";

import { useCtrlPressed } from "@/frameworks/core/hooks";
import { Button, Image } from "@/frameworks/designStuff/components";
import useDocumentViewerStore from "../../stores/useDocumentViewerStore";
import useDocumentViewerTransform from "../hooks/useDocumentViewerTransform";
import { WrapperBtnChangeImg, ImageViewerWrap } from "./emotion";
import { LoadingFile } from "../../emotion";
import { MAX_SCALE, DISTANCE_TO_SCREEN_EDGE } from "../constant";

import image1280x720 from "@/assets/images/loadingFailed/content.png";

interface ImageViewerProps {
	imageList: string[];
	currentIndex?: number;
	minimize?: boolean;
	handleChangePreview: () => void;
	handleChangeNext: () => void;
}

export default function ImageViewer({
	imageList,
	currentIndex = 0,
	handleChangePreview,
	handleChangeNext,
	minimize = false
}: ImageViewerProps) {
	const scale = useDocumentViewerStore((state) => state.scale);
	const prevScale = useDocumentViewerStore((state) => state.prevScale);
	const minScale = useDocumentViewerStore((state) => state.minScale);
	const resetScale = useDocumentViewerStore((state) => state.resetScale);

	const [imageLoaded, setImageLoaded] = useState(false);

	const transformWrapperRef = useRef<ReactZoomPanPinchRef | null>(null);
	const isWheelZooming = useRef(false);

	const { initializeViewer, updateViewerToCenter, handleWheel, updatePanning } =
		useDocumentViewerTransform(transformWrapperRef);
	const { isCtrlPressed } = useCtrlPressed();

	/* Load File */
	const imageSrc = useMemo(() => {
		return imageList[currentIndex];
	}, [currentIndex]);

	/* Initialize */
	useEffect(() => {
		if (imageLoaded) {
			const resolution = initializeViewer();
			EventHandler.fire("$Viewer::FileLoaded$", resolution, "transform-content");
		} else {
			resetScale();
		}
	}, [imageLoaded]);

	/* Resize Window */
	useEffect(() => {
		// resize가 될 때 문서를 화면의 센터로 맞춘다
		const handleResize = debounce(() => updateViewerToCenter(), 100);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [scale]);

	useEffect(() => {
		imageLoaded && updateViewerToCenter(scale, 0);
	}, [minimize]);

	/* Page */
	useEffect(() => {
		const onhandleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "ArrowLeft" && currentIndex > 0) handleChangeImage("prev");
			if (e.key === "ArrowRight" && currentIndex < imageList.length - 1) handleChangeImage("next");
		};
		window.addEventListener("keydown", onhandleKeyDown);
		return () => window.removeEventListener("keydown", onhandleKeyDown);
	}, [currentIndex, imageList.length]);

	const handleChangeImage = (typeChange: "next" | "prev") => {
		typeChange === "next" ? handleChangeNext() : handleChangePreview();
		setImageLoaded(false);
	};

	/* Scroll */
	const handleScroll = (e: WheelEvent) => {
		if (isCtrlPressed || scale === minScale) return;
		updatePanning(e.deltaY);
	};

	const handleZoomStateChanged = (e: ReactZoomPanPinchRef, isStart: boolean) => {
		// zoom out인데 minScale일 경우 center
		if (!isStart && e.state.scale === minScale) updateViewerToCenter(e.state.scale);
		isWheelZooming.current = isStart;
	};

	/* Update Transform */
	useEffect(() => {
		if (isWheelZooming.current) return;
		const factor = Math.log(scale / prevScale);
		if (scale > prevScale) transformWrapperRef.current?.zoomIn(factor, 0);
		else transformWrapperRef.current?.zoomOut(-factor, 0);
	}, [scale]);

	return (
		<>
			<Helmet>
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
				/>
			</Helmet>
			<ImageViewerWrap onWheel={handleScroll}>
				{currentIndex > 0 && (
					<WrapperBtnChangeImg css={{ left: DISTANCE_TO_SCREEN_EDGE }}>
						<Button.Icon
							filled="neutral"
							name="Left24"
							size="Xlarge"
							onClick={() => handleChangeImage("prev")}
						/>
					</WrapperBtnChangeImg>
				)}
				{currentIndex < imageList.length - 1 && (
					<WrapperBtnChangeImg css={{ right: DISTANCE_TO_SCREEN_EDGE }}>
						<Button.Icon
							filled="neutral"
							name="Right24"
							size="Xlarge"
							onClick={() => handleChangeImage("next")}
						/>
					</WrapperBtnChangeImg>
				)}
				{!imageLoaded && (
					<LoadingFile typoSize="Head5_B" typoColor="neutral_00_100">
						{_T("로딩중")}
					</LoadingFile>
				)}
				<TransformWrapper
					ref={transformWrapperRef}
					minScale={minScale}
					maxScale={MAX_SCALE}
					wheel={{ step: 1, disabled: !isCtrlPressed }}
					pinch={{ disabled: false, step: 0.05 }}
					panning={{ disabled: false }}
					doubleClick={{ disabled: true }}
					limitToBounds={true}
					centerOnInit={true}
					onWheel={handleWheel}
					onWheelStart={(e) => handleZoomStateChanged(e, true)}
					onWheelStop={(e) => handleZoomStateChanged(e, false)}
					centerZoomedOut={isWheelZooming.current} // wheel zooming일때 center 되는 것을 방지
					zoomAnimation={{ disabled: true }} // ctrl + wheel시 라이브러리 자체에서 처리되는 규칙을 무시하기 위함
				>
					<TransformComponent
						contentStyle={{
							visibility: "visible",
							position: "absolute"
						}}
					>
						<div id="transform-content">
							<Image
								loading="lazy"
								src={imageSrc}
								draggable={false}
								onLoad={() => setImageLoaded(true)}
								errorSrc={image1280x720}
								onError={(e) => console.error("#ImageViewer#", e)}
								style={{ pointerEvents: "auto" }}
							/>
						</div>
					</TransformComponent>
				</TransformWrapper>
			</ImageViewerWrap>
		</>
	);
}
